import type { FrameProps } from "@/components/shapes/shared/frame-props";
import type { AgentTemplate, ProjectAgent } from "@/api/agents";
import type { Frame, Project, Space } from "@/api/spaces";
import type { Task, TaskStatus } from "@/api/tasks";
import type {
  GlobeView,
  MemoryFact,
  MemoryThing,
  ThingKind,
} from "@/api/memory-focus";
import type { FrameData } from "@/lib/frame-data";

export type SceneId =
  | "logo"
  | "switcher"
  | "agent"
  | "voice"
  | "taskboard"
  | "bubble"
  | "html"
  | "memory"
  | "flat";

export type MediaItemId =
  | "logo"
  | "switcher"
  | "agent"
  | "taskboard"
  | "html"
  | "memory";

export const SCENES: Array<{ id: SceneId; duration: number }> = [
  { id: "logo", duration: 2100 },
  { id: "switcher", duration: 2300 },
  { id: "agent", duration: 2600 },
  { id: "voice", duration: 2100 },
  { id: "taskboard", duration: 2800 },
  { id: "bubble", duration: 2300 },
  { id: "html", duration: 2800 },
  { id: "memory", duration: 2500 },
  { id: "flat", duration: 999999 },
];

export const PROJECT_ID = "media-project-dolcetto";
export const SPACE_ID = "media-space-here";
export const MEDIA_CURSOR_AGENT_ID = "media-agent-denker";
export const MEDIA_INTRO_COPY = "Hi, I am Denker, your AI coworker.";

function nowIso(minutesAgo = 0): string {
  return new Date(Date.now() - minutesAgo * 60_000).toISOString();
}

export function demoProject(): Project {
  const now = nowIso();
  return {
    id: PROJECT_ID,
    name: "Dolcetto",
    description: "Media workspace",
    status: "active",
    color: "#30D158",
    is_pinned: true,
    sort_order: 0,
    is_home: true,
    parallel_sessions_enabled: true,
    created_at: now,
    updated_at: now,
  };
}

export function demoSpace(): Space {
  const now = nowIso();
  return {
    id: SPACE_ID,
    project: PROJECT_ID,
    name: "Here Media",
    ticket_prefix: "WEL",
    canvas_state: {},
    color: "#30D158",
    is_pinned: true,
    is_onboarding: false,
    sort_order: 0,
    created_at: now,
    updated_at: now,
  };
}

const AGENT_SPECS = [
  ["denker", "Denker", "minimax-m2.5", "opencode_cli", "#7C6DFF", true],
  ["analyst", "Analyst", "gpt-5.2", "codex_cli", "#FFD66B", false],
  ["assistant", "Assistant", "gpt-5.4-mini", "codex_cli", "#F8F55A", false],
  ["coder", "Coder", "claude-sonnet-4-6", "claude_cli", "#F7F45B", false],
  ["designer", "Designer", "claude-sonnet-4-6", "claude_cli", "#4FEA7D", false],
  ["hr", "HR", "gpt-5.4-mini", "codex_cli", "#FF6A4D", false],
  ["marketer", "Marketer", "claude-sonnet-4-6", "claude_cli", "#64D2FF", false],
  [
    "researcher",
    "Researcher",
    "claude-sonnet-4-6",
    "claude_cli",
    "#D76CFF",
    false,
  ],
  ["sales", "Sales", "claude-sonnet-4-6", "claude_cli", "#8EB8FF", false],
  ["writer", "Writer", "gpt-5.4-mini", "codex_cli", "#69E6F5", false],
] as const;

export function demoTemplates(): AgentTemplate[] {
  return AGENT_SPECS.map(([key, name, model, adapterType, color, builtin]) => ({
    id: `media-template-${key}`,
    name,
    role: `${name} agent`,
    color,
    avatar: "",
    avatar_seed: `media-${key}`,
    agent_md_content: "",
    adapter_type: adapterType,
    adapter_config: { model },
    status: "available",
    is_builtin: builtin,
    created_at: nowIso(60),
    updated_at: nowIso(5),
  }));
}

export function demoAgents(): ProjectAgent[] {
  return AGENT_SPECS.map(([key, name, , , color, builtin], index) => ({
    id: `media-agent-${key}`,
    project: PROJECT_ID,
    agent_template: `media-template-${key}`,
    project_specific_config: {},
    template_name: name,
    template_role: `${name} agent`,
    template_color: color,
    template_avatar: "",
    template_avatar_seed: `media-${key}`,
    is_template_builtin: builtin,
    created_at: nowIso(60 - index),
    updated_at: nowIso(5),
  }));
}

function demoTask({
  id,
  title,
  number,
  agent,
  status = "done",
  comments = 1,
}: {
  id: string;
  title: string;
  number: number;
  agent: "researcher" | "marketer" | "writer";
  status?: TaskStatus;
  comments?: number;
}): Task {
  const agentSpec = AGENT_SPECS.find(([key]) => key === agent)!;
  return {
    id,
    project: PROJECT_ID,
    title,
    description: "",
    source: "chat",
    status,
    priority: "medium",
    frame: null,
    text_anchor: null,
    assigned_agent: `media-agent-${agent}`,
    assigned_agent_detail: {
      id: `media-agent-${agent}`,
      name: agentSpec[1],
      color: agentSpec[4],
    },
    assigned_user: null,
    assigned_user_detail: null,
    blocked_by_detail: [],
    blocks_detail: [],
    comment_count: comments,
    last_comment:
      comments > 0
        ? {
            author: agentSpec[1],
            content: "Ready for review.",
            created_at: nowIso(3),
          }
        : null,
    is_checked_out: false,
    checkout_agent_key: "",
    ticket_number: number,
    ticket_display: `WEL-${number}`,
    parent_task: null,
    delegation_kind: "",
    child_tasks_summary: null,
    parent_task_summary: null,
    due_at: null,
    schedule: null,
    needs_attention: false,
    autonomous_attempts: 0,
    branch_name: null,
    base_branch: null,
    pr_target_branch: null,
    ship_phase: "",
    pr_url: "",
    pr_number: null,
    checks_status: "none",
    deliverable_frame_ids: [],
    created_at: nowIso(30 - number),
    updated_at: nowIso(2),
  };
}

export function demoTasks(): Task[] {
  return [
    demoTask({
      id: "media-task-21",
      title: "Research the latest AI agent trends",
      number: 21,
      agent: "researcher",
      comments: 1,
    }),
    demoTask({
      id: "media-task-22",
      title: "Using the Researcher findings, draft positioning",
      number: 22,
      agent: "marketer",
      comments: 3,
    }),
    demoTask({
      id: "media-task-23",
      title: "Comment on Why/How/What positioning",
      number: 23,
      agent: "researcher",
      comments: 1,
    }),
    demoTask({
      id: "media-task-24",
      title: "Comment on 60-second demo sequence",
      number: 24,
      agent: "researcher",
      comments: 1,
    }),
    demoTask({
      id: "media-task-25",
      title: "Comment on Make Time takeaway",
      number: 25,
      agent: "researcher",
      comments: 1,
    }),
    demoTask({
      id: "media-task-26",
      title: "Comment on onboarding memory graph",
      number: 26,
      agent: "researcher",
      comments: 1,
    }),
  ];
}

function frameData(frame: Frame): FrameData {
  return {
    frameId: frame.id,
    spaceId: frame.space,
    frameType: frame.frame_type,
    title: frame.title,
    content: frame.content,
    status: frame.status,
    agentId: frame.agent_id,
    parentFrame: frame.parent_frame,
    metadata: frame.metadata,
    createdAt: frame.created_at,
    taskTicket: null,
    editedByUserAt: frame.edited_by_user_at,
  };
}

export function frameProps(frame: Frame): FrameProps {
  return {
    id: frame.id,
    data: frameData(frame),
    selected: false,
  };
}

export function demoFrame(
  id: string,
  frameType: Frame["frame_type"],
  title: string,
  content: Record<string, unknown>,
  width: number,
  height: number,
  metadata: Record<string, unknown> = {},
): Frame {
  return {
    id,
    space: SPACE_ID,
    frame_type: frameType,
    title,
    content,
    status: "done",
    position_x: 0,
    position_y: 0,
    width,
    height,
    agent_id: null,
    parent_frame: null,
    metadata,
    created_at: nowIso(20),
    updated_at: nowIso(1),
    task_ticket_number: null,
    task_id: null,
    task_title: null,
    edited_by_user_at: null,
  };
}

const DEMO_THINGS: Array<[string, string, ThingKind]> = [
  ["memory-denker-ai", "Denker AI", "concept"],
  ["memory-make-time", "Make Time", "resource"],
  ["memory-jake", "Jake Knapp", "person"],
  ["memory-john", "John Zeratsky", "person"],
  ["memory-jane", "Jane", "person"],
  ["memory-source", "Make Time source page", "document"],
  ["memory-positioning", "Why/How/What positioning", "concept"],
  ["memory-workspace", "Context-aware AI workspace", "concept"],
  ["memory-founders", "Founders, solo builders, and small teams", "category"],
  ["memory-brief", "L-15 research brief", "document"],
  ["memory-saved", "Onboarding context saved", "concept"],
];
const DEMO_FACTS: Array<[string, string, string, string]> = [
  ["edge-1", "memory-denker-ai", "memory-workspace", "describes"],
  ["edge-2", "memory-denker-ai", "memory-founders", "serves"],
  ["edge-3", "memory-denker-ai", "memory-positioning", "frames"],
  ["edge-4", "memory-denker-ai", "memory-source", "remembers"],
  ["edge-5", "memory-denker-ai", "memory-make-time", "connects"],
  ["edge-6", "memory-make-time", "memory-jake", "authored_by"],
  ["edge-7", "memory-make-time", "memory-john", "authored_by"],
  ["edge-8", "memory-source", "memory-jane", "reviewed_by"],
  ["edge-9", "memory-user", "memory-brief", "requested"],
  ["edge-10", "memory-make-time", "memory-saved", "saved_as"],
];
/** The media page's memory: the demo things on the globe around You. */
export function demoMemoryGlobe(): GlobeView {
  const you: MemoryThing = {
    id: "memory-user",
    name: "profile",
    kind: "user_profile",
    description: "",
  };
  const things = [
    you,
    ...DEMO_THINGS.map(
      ([id, name, kind]): MemoryThing => ({
        id,
        name,
        kind,
        description: name,
      }),
    ),
  ];
  const byId = new Map(things.map((thing) => [thing.id!, thing]));
  const end = (id: string) => ({
    id,
    name: byId.get(id)!.name,
    kind: byId.get(id)!.kind,
  });
  const said = (id: string) => (id === you.id ? "You" : byId.get(id)!.name);
  const facts = DEMO_FACTS.map(
    ([id, source, target, relation]): MemoryFact => ({
      id,
      source: end(source),
      target: end(target),
      relationship_type: relation,
      claim: `${said(source)} ${relation.replace(/_/g, " ")} ${said(target)}.`,
      epistemic_status: "fact",
      scope_state: "global",
      qualifiers: [],
      created_at: nowIso(4),
      updated_at: nowIso(4),
    }),
  );
  return {
    memory_size: DEMO_THINGS.length,
    things: things.map((thing) => ({ thing, more_count: 0 })),
    facts,
    suggestions: [],
  };
}
